import { Link, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import ThemeSwitcher from '@/components/ThemeSwitcher';

interface LayoutProps {
  children: ReactNode;
}

const navLinks = [
  { to: '/', label: 'Home' },
  { to: '/gallery', label: 'Gallery' },
  { to: '/about', label: 'About' },
];

export default function Layout({ children }: LayoutProps) {
  const { pathname } = useLocation();

  return (
    <div className="flex min-h-screen flex-col bg-clean-cut-50">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:bg-white focus:p-4 focus:text-green-700"
      >
        Skip to main content
      </a>

      <header
        role="banner"
        className="sticky top-0 z-30 border-b border-clean-cut-100 bg-white/80 backdrop-blur-md"
      >
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-4">
          <Link
            to="/"
            className="flex items-center gap-3 text-xl font-bold tracking-tight text-gray-900"
            aria-label="Home"
          >
            <img src="/CLEANCUTLOGO.PNG" alt="Clean Cut Tree Care Logo" className="h-10 w-auto" />
            <span className="hidden sm:inline font-serif">Clean Cut Tree Care</span>
          </Link>
          <nav aria-label="Main navigation">
            <ul className="flex gap-1" role="list">
              {navLinks.map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    aria-current={pathname === to ? 'page' : undefined}
                    className={`rounded-xl px-3 py-2 text-sm font-medium transition-colors ${
                      pathname === to
                        ? 'bg-green-100 text-green-700'
                        : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                    }`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </header>

      <main id="main-content" role="main" className="mx-auto w-full max-w-7xl flex-1 px-4 py-10">
        {children}
      </main>

      <footer role="contentinfo" className="border-t border-clean-cut-100 bg-white/60">
        <div className="mx-auto max-w-7xl px-4 py-8">
          <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
            <p className="text-sm text-gray-600">
              &copy; {new Date().getFullYear()} Clean Cut Tree Care. All rights reserved.
            </p>
            <p className="text-sm font-medium text-green-700">(303) 717-4388</p>
          </div>
        </div>
      </footer>

      {import.meta.env.DEV && <ThemeSwitcher />}
    </div>
  );
}
